import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ReactNode, useEffect, lazy } from 'react';
import { Spin } from 'antd';
import { RequireAuth } from '../components/RequireAuth';
import RouteError from '../components/RouteError';
import { usePlatformAuth } from '../stores/auth.platform';
import { platformApi } from '../api/platform';
import { firstAllowedPath } from '../constants/permissions';
import PlatformLoginPage from '../pages/login/PlatformLoginPage';
import PlatformLayout from '../layouts/PlatformLayout';
const DashboardPage = lazy(() => import('../pages/platform/DashboardPage'));
const TenantListPage = lazy(() => import('../pages/platform/TenantListPage'));
const TenantCreatePage = lazy(() => import('../pages/platform/TenantCreatePage'));
const OrderListPage = lazy(() => import('../pages/platform/OrderListPage'));
const OrderDetailPage = lazy(() => import('../pages/platform/OrderDetailPage'));
const PlanListPage = lazy(() => import('../pages/platform/PlanListPage'));
const PlanEditPage = lazy(() => import('../pages/platform/PlanEditPage'));
const InvoiceReviewPage = lazy(() => import('../pages/platform/InvoiceReviewPage'));
const PendingChargesPage = lazy(() => import('../pages/platform/PendingChargesPage'));
const BankTransferReviewPage = lazy(() => import('../pages/platform/BankTransferReviewPage'));
const FinanceDashboardPage = lazy(() => import('../pages/platform/FinanceDashboardPage'));
const SystemConfigPage = lazy(() => import('../pages/platform/SystemConfigPage'));
const OperationsChatSuggestionsPage = lazy(() => import('../pages/platform/OperationsChatSuggestionsPage'));
const IndividualUserListPage = lazy(() => import('../pages/platform/IndividualUserListPage'));
const IndividualUserDetailPage = lazy(() => import('../pages/platform/IndividualUserDetailPage'));
const GroupRelationsPage = lazy(() => import('../pages/platform/GroupRelationsPage'));
const GroupTreePage = lazy(() => import('../pages/platform/GroupTreePage'));
const KeywordAlertsPage = lazy(() => import('../pages/platform/KeywordAlertsPage'));
const AppVisitsPage = lazy(() => import('../pages/platform/AppVisitsPage'));
const CardChannelsPage = lazy(() => import('../pages/platform/CardChannelsPage'));
const AiUsageDashboardPage = lazy(() => import('../pages/platform/AiUsageDashboardPage'));
const MessagesArchivePage = lazy(() => import('../pages/platform/MessagesArchivePage'));
const AuditConfigPage = lazy(() => import('../pages/platform/AuditConfigPage'));
const RechargePromotionsPage = lazy(() => import('../pages/platform/RechargePromotionsPage'));
const MallProductsPage = lazy(() => import('../pages/platform/MallProductsPage'));
const CmsListPage = lazy(() => import('../pages/platform/CmsListPage'));
const CmsEditPage = lazy(() => import('../pages/platform/CmsEditPage'));
const LegalDocsPage = lazy(() => import('../pages/platform/LegalDocsPage'));
const FeedbackListPage = lazy(() => import('../pages/platform/FeedbackListPage'));
const FeedbackReplyPage = lazy(() => import('../pages/platform/FeedbackReplyPage'));
const ThemesPage = lazy(() => import('../pages/platform/ThemesPage'));
const CardTemplatesPage = lazy(() => import('../pages/platform/CardTemplatesPage'));
const StaffListPage = lazy(() => import('../pages/platform/StaffListPage'));
const StaffEditPage = lazy(() => import('../pages/platform/StaffEditPage'));
const RoleListPage = lazy(() => import('../pages/platform/RoleListPage'));
const RoleEditPage = lazy(() => import('../pages/platform/RoleEditPage'));
const AuditLogsPage = lazy(() => import('../pages/platform/AuditLogsPage'));
const LoginLogsPage = lazy(() => import('../pages/platform/LoginLogsPage'));
const EnterpriseShopPage = lazy(() => import('../pages/platform/EnterpriseShopPage'));
const GeoEntitlementsPage = lazy(() => import('../pages/platform/geo/EntitlementsPage'));
const GeoRuleTemplatesPage = lazy(() => import('../pages/platform/geo/RuleTemplatesPage'));
const RagKnowledgePage = lazy(() => import('../pages/platform/RagKnowledgePage'));
const AigcMediaPage = lazy(() => import('../pages/platform/AigcMediaPage'));
const ServiceHallPage = lazy(() => import('../pages/platform/ServiceHallPage'));

function Auth({ children }: { children: ReactNode }) {
  const jwt = usePlatformAuth(s => s.jwt);
  const user = usePlatformAuth(s => s.user);
  const setAuth = usePlatformAuth(s => s.setAuth);
  const logout = usePlatformAuth(s => s.logout);

  // store 仅持久化 jwt；刷新后 user/permissions 丢失 → 用 jwt 调 /me 重新水合，
  // 否则所有 useHasPermission 因 user=null 返回 false，按钮被错误 disable。
  useEffect(() => {
    if (!jwt || user) return;
    platformApi.me()
      .then(({ permissions, ...rest }) => setAuth(jwt, rest, permissions ?? []))
      .catch(() => logout());
  }, [jwt, user, setAuth, logout]);

  if (jwt && !user) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }
  return <RequireAuth jwt={jwt} loginPath="/login">{children}</RequireAuth>;
}

/** 首页落点：RBAC 后不再写死 /dashboard，跳当前角色第一个有权限的菜单页 */
function HomeRedirect() {
  const permissions = usePlatformAuth((s) => s.permissions);
  return <Navigate to={firstAllowedPath(permissions) ?? '/dashboard'} replace />;
}

export const platformRouter = createBrowserRouter([
  {
    // pathless 根路由：统一 errorElement 兜底所有子路由抛出的渲染错误 /
    // chunk 加载失败，取代 React Router 默认的 "Unexpected Application Error!"。
    errorElement: <RouteError />,
    children: [
  { path: '/login', element: <PlatformLoginPage /> },
  {
    path: '/',
    element: <Auth><PlatformLayout /></Auth>,
    children: [
      { index: true, element: <HomeRedirect /> },
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'tenants', element: <TenantListPage /> },
      { path: 'tenants/new', element: <TenantCreatePage /> },
      { path: 'tenants/:id/edit', element: <TenantCreatePage /> },
      { path: 'tenants/:tenantId/enterprise-shop', element: <EnterpriseShopPage /> },
      { path: 'tenant-relations', element: <GroupRelationsPage /> },
      { path: 'group-relations', element: <GroupTreePage /> },
      { path: 'individual-users', element: <IndividualUserListPage /> },
      { path: 'individual-users/:id', element: <IndividualUserDetailPage /> },
      { path: 'orders', element: <OrderListPage /> },
      { path: 'orders/:id', element: <OrderDetailPage /> },
      { path: 'plans', element: <PlanListPage /> },
      { path: 'geo-content/entitlements', element: <GeoEntitlementsPage /> },
      { path: 'geo-content/templates', element: <GeoRuleTemplatesPage /> },
      { path: 'plans/new', element: <PlanEditPage /> },
      { path: 'plans/:id/edit', element: <PlanEditPage /> },
      { path: 'invoices', element: <InvoiceReviewPage /> },
      { path: 'bank-transfer-reviews', element: <BankTransferReviewPage /> },
      { path: 'pending-charges', element: <PendingChargesPage /> },
      { path: 'finance', element: <FinanceDashboardPage /> },
      { path: 'system-config', element: <SystemConfigPage /> },
      { path: 'operations/chat-suggestions', element: <OperationsChatSuggestionsPage /> },
      { path: 'keyword-alerts', element: <KeywordAlertsPage /> },
      { path: 'app-visits', element: <AppVisitsPage /> },
      { path: 'card-channels', element: <CardChannelsPage /> },
      { path: 'ai-usage', element: <AiUsageDashboardPage /> },
      { path: 'rag', element: <RagKnowledgePage /> },
      { path: 'aigc', element: <AigcMediaPage /> },
      { path: 'service-hall', element: <ServiceHallPage /> },
      { path: 'messages-archive', element: <MessagesArchivePage /> },
      { path: 'audit-config', element: <AuditConfigPage /> },
      { path: 'recharge-promotions', element: <RechargePromotionsPage /> },
      { path: 'mall-products', element: <MallProductsPage /> },
      { path: 'cms', element: <CmsListPage /> },
      { path: 'cms/new', element: <CmsEditPage /> },
      { path: 'cms/:id/edit', element: <CmsEditPage /> },
      { path: 'legal-docs', element: <LegalDocsPage /> },
      // 三个独立菜单项 —— 同 page 锁定不同 doc_type
      { path: 'legal-docs/privacy_policy', element: <LegalDocsPage fixedType="privacy_policy" /> },
      { path: 'legal-docs/voiceprint_agreement', element: <LegalDocsPage fixedType="voiceprint_agreement" /> },
      { path: 'legal-docs/tos', element: <LegalDocsPage fixedType="tos" /> },
      { path: 'legal-docs/about_us', element: <LegalDocsPage fixedType="about_us" /> },
      { path: 'feedbacks', element: <FeedbackListPage /> },
      { path: 'feedbacks/:id/reply', element: <FeedbackReplyPage /> },
      { path: 'themes', element: <ThemesPage /> },
      { path: 'card-templates', element: <CardTemplatesPage /> }, // 名片模版
      { path: 'staff', element: <StaffListPage /> },
      { path: 'staff/:id/edit', element: <StaffEditPage /> },
      { path: 'roles', element: <RoleListPage /> },
      { path: 'roles/:id/edit', element: <RoleEditPage /> },
      { path: 'audit-logs', element: <AuditLogsPage /> },
      { path: 'login-logs', element: <LoginLogsPage /> },
    ],
  },
  { path: '*', element: <div style={{padding: 40}}>404 Not Found</div> },
    ],
  },
]);
